import { Icon, Modal } from '@deity-io/portal-ui';
import { EntityNameDescriptionFields } from 'components/FormFields';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useCreatePackageMutation, useUpdatePackageMutation } from 'services/graphql/generated/hooks';
import { v4 } from 'uuid';
import * as Yup from 'yup';

interface CreateEditPackageModalProps {
  isOpen: boolean;
  isEdit?: boolean;
  editPackage?: { uuid: string; name: string; description: string };
  setOpenState: (state: boolean) => void;
  onPackageCreated: () => void;
}

export interface CreateEditPackageInput {
  name: string;
  description: string;
}
/**
 * todo : get companyUuid from User
 */
const companyUuid = '3fa85f64-5717-4562-b3fc-2c963f66afa6';

export const CreateEditPackageModal: FC<CreateEditPackageModalProps> = ({
  isOpen,
  setOpenState,
  onPackageCreated,
  isEdit,
  editPackage
}) => {
  const [create, { data: createData, loading: createLoading, error: createError }] = useCreatePackageMutation();
  const [update, { data: updateData, loading: updateLoading, error: updateError }] = useUpdatePackageMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [newPackageKey, setNewPackageKey] = useState<string>(v4());

  const CreatePackageInputSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too short').max(50, 'Too long').required('This is a required field.'),
    description: Yup.string().min(2, 'Too short').max(50, 'Too long')
  });

  useEffect(() => {
    if (createData || updateData) {
      onPackageCreated();
      setOpenState(false);
    }
    if (createError || updateError) {
      setErrorMessage(createError?.message ?? updateError?.message ?? null);
    }
    if (createData) {
      setNewPackageKey(v4());
    }
  }, [createData, updateData, createError, updateError]);

  const createOrUpdate = (input: CreateEditPackageInput) => {
    if (!input.description) {
      input.description = '';
    }

    return isEdit ? updatePackage(input) : createPackage(input);
  };

  const createPackage = (input: CreateEditPackageInput) => {
    create({
      variables: {
        uuid: newPackageKey,
        companyUuid,
        packageVersionUuid: v4(),
        name: input.name,
        description: input.description
      }
    }).catch(err => {
      setErrorMessage(err.message);
    });
  };

  const updatePackage = (input: CreateEditPackageInput) => {
    update({
      variables: {
        uuid: editPackage?.uuid ?? v4(),
        companyUuid,
        name: input.name,
        description: input.description
      }
    })
      .then(() => {
        // handle successful update here, if needed
      })
      .catch(err => {
        setErrorMessage(err.message);
      });
  };

  const { values, setValues, setFieldValue, setFieldTouched, errors, handleSubmit } = useFormik({
    initialValues: {
      name: editPackage?.name ?? '',
      description: editPackage?.description ?? ''
    },
    validateOnBlur: true,
    validationSchema: CreatePackageInputSchema,
    onSubmit: v => createOrUpdate(v)
  });

  useEffect(() => {
    if (editPackage) {
      setValues({ name: editPackage?.name!, description: editPackage?.description! });
    }
  }, [editPackage]);

  const confirmText = () => {
    if (!isEdit) {
      return `${createLoading ? 'Creating' : 'Create'} Package`;
    }

    return `${updateLoading ? 'Updating' : 'Update'} Package`;
  };

  if (isEdit && !editPackage) {
    return null!;
  }
  const packageKey = editPackage?.uuid || newPackageKey;

  return (
    <Modal
      key={packageKey}
      isOpen={isOpen}
      onClose={() => {
        setOpenState(false);
      }}
      label={`${isEdit ? 'Edit' : 'New'} Package`}
      title="Enter a name and description."
      closeIcon={<Icon icon="close" />}
      confirmText={confirmText()}
      onConfirm={() => handleSubmit()}
    >
      {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}
      <form onSubmit={handleSubmit}>
        <EntityNameDescriptionFields
          errors={errors}
          values={values}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          entity="Package"
        />
      </form>
    </Modal>
  );
};
